import { useCallback, useEffect, useState } from 'react';
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import Notification, { NotificationContainer } from '../../../components/Notification';
import UserPicker from '../../../components/UserPicker';
import tprmService from '../../../services/compliance/tprm.service';
import {
  RemediationAction,
  RemediationActionCreate,
  RemediationActionUpdate,
  RemediationStatus,
} from '../../../types/compliance';
import ModalHeader from '../../../components/ui/ModalHeader';
import { useModalDrag } from '../../../hooks/useModalDrag';
import { useUserNames } from '../../../hooks/useUserNames';

interface Props {
  vendorId: string;
}

const STATUS_LABELS: Record<RemediationStatus, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  completed: 'Completed',
  waived: 'Waived',
};

const STATUS_BADGE: Record<RemediationStatus, string> = {
  open: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  in_progress: 'bg-blue-200 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  completed: 'bg-green-200 text-green-800 dark:bg-green-900 dark:text-green-200',
  waived: 'bg-amber-200 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
};

type FormState = {
  title: string;
  description: string;
  due_date: string;
  status: RemediationStatus;
  assigned_to_oid: string;
};

const emptyForm: FormState = {
  title: '',
  description: '',
  due_date: '',
  status: 'open',
  assigned_to_oid: '',
};

function rowToForm(r: RemediationAction): FormState {
  return {
    title: r.title,
    description: r.description || '',
    due_date: r.due_date || '',
    status: r.status,
    assigned_to_oid: r.assigned_to_oid || '',
  };
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString();
  } catch {
    return iso;
  }
}

export default function RemediationsPanel({ vendorId }: Props) {
  const { nameFor } = useUserNames();
  const [rows, setRows] = useState<RemediationAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(modalOpen);
  const [editing, setEditing] = useState<RemediationAction | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [errorNotification, setErrorNotification] =
    useState<{ title: string; message: string } | null>(null);
  const [successNotification, setSuccessNotification] =
    useState<{ title: string; message: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await tprmService.listRemediations(vendorId);
      setRows(data);
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Failed to load remediations', message: msg });
    } finally {
      setLoading(false);
    }
  }, [vendorId]);

  useEffect(() => {
    load();
  }, [load]);

  // ESC-to-close
  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setModalOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalOpen]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (r: RemediationAction) => {
    setEditing(r);
    setForm(rowToForm(r));
    setModalOpen(true);
  };

  const handleDelete = async (r: RemediationAction) => {
    if (!window.confirm(`Delete remediation "${r.title}"?`)) return;
    try {
      await tprmService.deleteRemediation(vendorId, r.id);
      setSuccessNotification({ title: 'Deleted', message: `Remediation "${r.title}" deleted.` });
      load();
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Failed to Delete', message: msg });
    }
  };

  const handleSave = async () => {
    const trimmedTitle = form.title.trim();
    if (!trimmedTitle) {
      setErrorNotification({ title: 'Title required', message: 'Please provide a title.' });
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const body: RemediationActionUpdate = {
          title: trimmedTitle,
          description: form.description,
          due_date: form.due_date || null,
          status: form.status,
          assigned_to_oid: form.assigned_to_oid.trim() || null,
        };
        await tprmService.updateRemediation(vendorId, editing.id, body);
        setSuccessNotification({ title: 'Updated', message: `Remediation "${trimmedTitle}" updated.` });
      } else {
        const body: RemediationActionCreate = {
          title: trimmedTitle,
          description: form.description,
          due_date: form.due_date || null,
          status: form.status,
          assigned_to_oid: form.assigned_to_oid.trim() || null,
        };
        await tprmService.createRemediation(vendorId, body);
        setSuccessNotification({ title: 'Created', message: `Remediation "${trimmedTitle}" created.` });
      }
      setModalOpen(false);
      load();
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Failed to Save', message: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold">Remediation Actions</h2>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
        >
          <PlusIcon className="w-4 h-4" />
          New Remediation
        </button>
      </div>

      {loading ? (
        <div className="text-center py-6 text-gray-500">Loading remediations...</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-6 text-gray-500 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
          No remediation actions yet.
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr className="text-left">
                <th className="py-2 px-3">Title</th>
                <th className="py-2 px-3">Due Date</th>
                <th className="py-2 px-3">Status</th>
                <th className="py-2 px-3">Assigned</th>
                <th className="py-2 px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <td className="py-2 px-3">
                    <div className="font-medium">{r.title}</div>
                    {r.description && (
                      <div className="text-xs text-gray-500 mt-0.5 line-clamp-2" title={r.description}>
                        {r.description}
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-3">{formatDate(r.due_date)}</td>
                  <td className="py-2 px-3">
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded ${STATUS_BADGE[r.status]}`}
                    >
                      {STATUS_LABELS[r.status]}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-xs text-gray-500">
                    {nameFor(r.assigned_to_oid) || '—'}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <div className="inline-flex gap-2">
                      <button
                        onClick={() => openEdit(r)}
                        className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400"
                        title="Edit"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(r)}
                        className="p-1 text-red-600 hover:text-red-800 dark:text-red-400"
                        title="Delete"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/40">
          <div
            ref={panelRef}
            style={panelStyle}
            className="bg-panel rounded-lg w-full max-w-lg border border-gray-200 dark:border-gray-700"
          >
            <ModalHeader
              title={editing ? 'Edit Remediation' : 'New Remediation'}
              onClose={saving ? undefined : () => setModalOpen(false)}
              dragHandleProps={dragHandleProps}
            />
            <div className="p-6">
            <label className="block text-sm font-medium mb-1">
              Title <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              maxLength={255}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 mb-3"
            />

            <label className="block text-sm font-medium mb-1">Due Date</label>
            <input
              type="date"
              value={form.due_date}
              onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 mb-3"
            />

            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={3}
              maxLength={5000}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 mb-3"
            />

            <label className="block text-sm font-medium mb-1">Assigned To</label>
            <div className="mb-3">
              <UserPicker
                value={form.assigned_to_oid}
                onChange={(oid) => setForm((f) => ({ ...f, assigned_to_oid: oid }))}
                placeholder="Search by name or email (optional)…"
                emailDomain="@gwii.com"
              />
            </div>

            <label className="block text-sm font-medium mb-1">Status</label>
            <select
              value={form.status}
              onChange={(e) =>
                setForm((f) => ({ ...f, status: e.target.value as RemediationStatus }))
              }
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 mb-4"
            >
              {(Object.keys(STATUS_LABELS) as RemediationStatus[]).map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS[s]}
                </option>
              ))}
            </select>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                disabled={saving}
                className="px-3 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !form.title.trim()}
                className="px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
              >
                {saving ? 'Saving...' : editing ? 'Save' : 'Create'}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {errorNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="error"
            title={errorNotification.title}
            message={errorNotification.message}
            showClose={true}
            onClose={() => setErrorNotification(null)}
            autoDismiss={5000}
          />
        </NotificationContainer>
      )}

      {successNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="success"
            title={successNotification.title}
            message={successNotification.message}
            showClose={true}
            onClose={() => setSuccessNotification(null)}
            autoDismiss={3000}
          />
        </NotificationContainer>
      )}
    </div>
  );
}
